import { FC, useContext, useEffect, useState } from 'react';
import styles from './DocumentUploadItem.module.scss';
import { faEye } from '@fortawesome/pro-solid-svg-icons';
import { faTrash } from '@fortawesome/pro-light-svg-icons';
import { FILE_UPLOAD_STATUS, IUpload, TDocumentUpload } from '@wohnsinn/ws-ts-lib';
import { UPLOAD_TYPE } from '../index';
import UserContext from '../../../../core/context/user.context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { wohnsinnServices } from '../../../../App';
import { LinearProgress } from '@mui/material';
import Text, { TEXT_WEIGHT } from '../../../atoms/typographie/Text';
import { MODAL_IDENTIFIER } from '../../../../core/enum/modals.enum';
import ModalContext from '../../../../core/context/modal.context';
import { useTranslation } from 'react-i18next';

const DocumentUploadItem: FC<{
  uploadItemData: IUpload;
  uploadType: UPLOAD_TYPE;
  onDocumentDelete?: (upload: IUpload, uploadType: UPLOAD_TYPE) => void;
  documentName?: string;
}> = ({ uploadItemData, uploadType, onDocumentDelete, documentName }) => {
  const { tenantProfile } = useContext(UserContext);
  const { firebaseStorageService, tenantService } = wohnsinnServices;
  const [progress, setProgress] = useState<number>(100);
  const { openModal } = useContext(ModalContext);
  const { t } = useTranslation('common', { keyPrefix: 'component.molecules.DocumentUploadItem' });

  useEffect(() => {
    uploadItemData?.uploadTask?.on(
      'state_changed',
      () => {
        setProgress(
          parseInt(
            (
              (uploadItemData.uploadTask.snapshot.bytesTransferred / uploadItemData.uploadTask.snapshot.totalBytes) *
              100
            ).toFixed(0)
          )
        );
      },
      async (err) => {
        uploadItemData.errors.push(err.message);
      },
      async () => {
        const uploadDocument: TDocumentUpload = {
          creatorId: uploadItemData.creatorId,
          extension: uploadItemData.extension,
          fileName: uploadItemData.fileName,
          url: uploadItemData.url,
          id: uploadItemData.id,
          alt: uploadItemData.fileName,
          mediaType: uploadItemData.mediaType,
          updatedAt: new Date(),
          uploadPath: uploadItemData.uploadPath,
        };

        switch (uploadType) {
          case UPLOAD_TYPE.INCOME_PROOF:
            return tenantService.addIncomeProofDocument(tenantProfile.uid, uploadDocument);

          case UPLOAD_TYPE.SCHUFA:
            return tenantService.addSchufaCheckDocument(tenantProfile.uid, uploadDocument);

          case UPLOAD_TYPE.PRIVATE_INSURANCE_DOCUMENT:
            return tenantService.addPrivateInsuranceDocument(tenantProfile.uid, uploadDocument);
        }
      }
    );
  }, [tenantProfile]);

  const deleteDocumentHandler = () => {
    switch (uploadType) {
      case UPLOAD_TYPE.INCOME_PROOF:
        return tenantService.deleteIncomeProofDocument(tenantProfile.uid, uploadItemData);
      case UPLOAD_TYPE.SCHUFA:
        return tenantService.deleteSchufaCheckDocument(tenantProfile.uid, uploadItemData);
      case UPLOAD_TYPE.PRIVATE_INSURANCE_DOCUMENT:
        return tenantService.deletePrivateInsuranceDocument(tenantProfile.uid, uploadItemData);
      default:
        return;
    }
  };

  const downloadDocument = () => {
    const windowReference = window.open();
    firebaseStorageService.getDownloadUrl(`${uploadItemData.uploadPath}`).then((href) => {
      windowReference.location = href;
    });
  };

  const cancelDocumentUpload = async () => {
    uploadItemData.uploadTask.cancel();
    onDocumentDelete(uploadItemData, uploadType);
  };

  return (
    <div className={styles.wrapper}>
      {progress < 100 ? (
        <LinearProgress
          variant="determinate"
          value={uploadItemData.status === FILE_UPLOAD_STATUS.CREATED ? progress : 100}
        />
      ) : null}

      <div className={styles.uploadItem}>
        <div className={styles.uploadItemContent}>
          <div className={styles.label}>
            <img
              src={'/assets/icon/bookmark_pictogram.svg'}
              width={30}
              alt={documentName ? documentName : 'Dokument'}
            />
            <Text tag={'span'} weight={TEXT_WEIGHT.TEXT_WEIGHT_REGULAR}>
              {documentName ? documentName : 'Dokument'}
            </Text>
          </div>
          <div className={styles.buttonWrapper}>
            {progress === 100 ? (
              <button
                onClick={() => downloadDocument()}
                type={'button'}
                className={styles.uploadActionButton}
                aria-label={`Button lädt herunter ${uploadItemData.alt}`}
              >
                <span className="sr-only">Herunterladen</span>
                <FontAwesomeIcon icon={faEye} />
              </button>
            ) : (
              <button
                onClick={() => cancelDocumentUpload()}
                type={'button'}
                className={styles.uploadActionButton}
                aria-label={`Upload von ${uploadItemData.alt} abbrechen`}
              >
                <span>Abbrechen</span>
              </button>
            )}
            <button
              onClick={() =>
                openModal({
                  id: MODAL_IDENTIFIER.DELETE_MODAL,
                  data: {
                    deleteHandler: () => deleteDocumentHandler(),
                    title: t('delete.title'),
                    text: t('delete.text'),
                  },
                })
              }
              type={'button'}
              className={styles.uploadActionButton}
              aria-label={`Klick löscht ${uploadItemData.alt}`}
            >
              <span className="sr-only">Löschen</span>
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadItem;
